// WHAT: Component for displaying a single Kanban column with roadmap items
// WHY:  Provides a reusable column component for Kanban board layout
// DOMAIN: Admin → Roadmap → Kanban
// DEPENDENCIES: Stack, Button, Card, Badge, ProgressBar components, Roadmap_Item type
// RISKS:
//   - NOTE: Fixed width (min-w-80 max-w-80) may not be responsive on small screens
//   - NOTE: Tags are limited to 2 visible - remaining shown as count
//   - NOTE: Priority color mapping may not match all priority values

import {
  LucidePlus,
  LucideCheckCircle,
  LucideClock,
  LucideAlertTriangle,
  LucideTarget,
} from "lucide-react";
import React from "react";

import { Stack, Button, Card, Badge, ProgressBar } from "@/components/ui";

import { type Roadmap_Item } from "../../../types/roadmap-types";

/**
 * Props for KanbanColumn component
 */
interface KanbanColumnProperties {
  /** Array of roadmap items in this column */
  items: Roadmap_Item[];
  /** Module name for add item functionality */
  module: string;
  /** Callback when add button is clicked */
  onAddItem: (module: string) => void;
  /** Callback when item is clicked */
  onItemClick: (item: Roadmap_Item) => void;
  /** Status value for this column */
  status: string;
  /** Column title to display */
  title: string;
}

/**
 * WHAT:
 *   Returns the appropriate icon component for a given status.
 *
 * WHY:
 *   Provides visual consistency for status representation.
 *
 * BEHAVIOR:
 *   - Maps known statuses to specific icons
 *   - Returns default icon for unknown statuses
 *
 * @param status - Status string to get icon for
 *
 * @returns {React.ComponentType<{ className?: string }>} Icon component for the status
 *
 */
const getStatusIcon = (status: string) => {
  switch (status) {
    case "open": {
      return LucideTarget;
    }
    case "in_progress": {
      return LucideClock;
    }
    case "done": {
      return LucideCheckCircle;
    }
    case "rejected": {
      return LucideAlertTriangle;
    }
    default: {
      return LucideTarget;
    }
  }
};

/**
 * WHAT:
 *   Returns the border color class for a given status.
 *
 * WHY:
 *   Provides visual distinction between different status columns.
 *
 * BEHAVIOR:
 *   - Maps known statuses to specific border colors
 *   - Returns default color for unknown statuses
 *
 * @param status - Status string to get color for
 *
 * @returns {string} Border color class name
 *
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case "open": {
      return "border-tracq-bright-blue";
    }
    case "in_progress": {
      return "border-tracq-deep-orange";
    }
    case "done": {
      return "border-green-500";
    }
    case "rejected": {
      return "border-red-500";
    }
    default: {
      return "border-slate-600";
    }
  }
};

/**
 * WHAT:
 *   Returns the background color class for a given priority.
 *
 * WHY:
 *   Provides visual indication of item priority.
 *
 * BEHAVIOR:
 *   - Maps known priorities to specific colors
 *   - Returns default color for unknown priorities
 *
 * @param priority - Priority string to get color for
 *
 * @returns {string} Background color class name
 *
 */
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high": {
      return "bg-tracq-deep-orange";
    }
    case "medium": {
      return "bg-yellow-500";
    }
    case "low": {
      return "bg-green-500";
    }
    default: {
      return "bg-slate-600";
    }
  }
};

/**
 * WHAT:
 *   Component that displays a single Kanban column with roadmap items,
 *   including header, item cards, and empty state.
 *
 * WHY:
 *   Encapsulates column rendering logic for Kanban board, providing a
 *   consistent interface for displaying items by status.
 *
 * PROPS:
 *   - title: string — Column title
 *   - status: string — Status value for this column
 *   - items: Roadmap_Item[] — Items to display in column
 *   - module: string — Module name for add item callback
 *   - onItemClick: (item: Roadmap_Item) => void — Handler for item clicks
 *   - onAddItem: (module: string) => void — Handler for add button clicks
 *
 * BEHAVIOR:
 *   - Displays column header with status icon, title, item count, and add button
 *   - Renders item cards with title, module, type, description, progress, and tags
 *   - Shows empty state when no items
 *   - Maps status to icon and border color
 *   - Maps priority to color indicator
 *
 * RISKS:
 *   - NOTE: Fixed column width may cause horizontal scroll on small screens
 *   - NOTE: Tags limited to 2 visible - users may not see all tags
 *   - NOTE: Priority color mapping assumes specific priority values
 *   - NOTE: Progress bar only shown if progress > 0 - 0% progress is hidden
 *
 * @param root0
 *
 * @param root0.title
 *
 * @param root0.status
 *
 * @param root0.items
 *
 * @param root0.module
 *
 * @param root0.onItemClick
 *
 * @param root0.onAddItem
 *
 * @returns {JSX.Element} Die gerenderte KanbanColumn-Komponente
 *
 */
export const KanbanColumn: React.FC<KanbanColumnProperties> = ({
  items,
  module,
  onAddItem,
  onItemClick,
  status,
  title,
}) => {
  const IconComponent = getStatusIcon(status);

  return (
    <div className="flex flex-col min-w-80 max-w-80">
      {/* Column Header */}
      <div
        className={`p-4 rounded-tracq border-t-4 ${getStatusColor(status)} bg-slate-700/30 shadow-tracq-card`}
      >
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={2}>
            <IconComponent className="w-5 h-5 text-slate-300" />
            <h3 className="font-semibold text-white">{title}</h3>
            <Badge size="sm" style="subtle" variant="default">
              {items.length}
            </Badge>
          </Stack>
          <Button
            className="p-1 text-slate-400 hover:text-white hover:bg-slate-600/50 rounded-lg transition-all duration-200"
            onClick={() => {
              onAddItem(module);
            }}
            size="sm"
            variant="ghost"
          >
            <LucidePlus className="w-4 h-4" />
          </Button>
        </Stack>
      </div>

      {/* Column Items */}
      <div className="flex-1 p-2 space-y-2 min-h-96">
        {items.map((item) => (
          <Card
            className="bg-slate-600/30 border-slate-500/50 shadow-tracq-card cursor-pointer hover:bg-slate-600/50 transition-all duration-200"
            key={item.id}
            onClick={() => {
              onItemClick(item);
            }}
            padding="md"
            variant="default"
          >
            <Stack direction="col" gap={2}>
              {/* Item Header */}
              <Stack align="start" direction="row" justify="between">
                <h4 className="font-medium text-white text-sm line-clamp-2">{item.title}</h4>
                <div
                  className={`w-2 h-2 rounded-full ${getPriorityColor(item.priority ?? "medium")} flex-shrink-0`}
                />
              </Stack>

              {/* Item Details */}
              <div className="space-y-1">
                <div className="text-xs text-slate-300">
                  {item.module} • {item.type}
                </div>
                {item.description ? (
                  <p className="text-xs text-slate-400 line-clamp-2">{item.description}</p>
                ) : null}
              </div>

              {/* Progress Bar */}
              {item.progress !== undefined && item.progress > 0 && (
                <ProgressBar
                  max={100}
                  showLabel={false}
                  size="sm"
                  state="default"
                  value={item.progress}
                />
              )}

              {/* Tags */}
              {item.tags && item.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {item.tags.slice(0, 2).map((tag) => (
                    <Badge key={tag} size="sm" style="subtle" variant="default">
                      {tag}
                    </Badge>
                  ))}
                  {item.tags.length > 2 && (
                    <Badge size="sm" style="subtle" variant="default">
                      +{item.tags.length - 2}
                    </Badge>
                  )}
                </div>
              ) : null}
            </Stack>
          </Card>
        ))}

        {/* Empty State */}
        {items.length === 0 && (
          <div className="flex items-center justify-center h-32 text-slate-400">
            <div className="text-center">
              <IconComponent className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No items</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
